/**
 * Limites dos anexos do composer do chat.
 *
 * ⚠️ Números PRÓPRIOS, e não os de `@modules/tarefas/constants/Attachments`.
 * Lá a constante se chama `MAX_ATTACHMENTS_PER_COMMENT` — importar "por
 * comentário" para dentro de um chat carregaria semântica errada só para não
 * repetir um `5`. As FUNÇÕES puras daquele módulo (`isImageAttachment`,
 * `getAttachmentIcon`) essas sim são reusadas: elas são genéricas de verdade e
 * o mapa de ícones que a segunda consulta é do `aawz-drive`.
 *
 * ★ [spec-016 T023] O gap G3 fechou PARCIALMENTE — medido, não presumido.
 * O composer agora faz upload real (`send-message-anexos-upload-mixin.js`)
 * via `POST /fee-fixo/clientes/carteira-chat/upload`, e o `upload_id` viaja
 * no corpo do turno (`apiChat._buildChatBody` → campo `anexos`). Mas esse é
 * o único endpoint de upload que existe hoje, e ele é o canal de EXTRATO DE
 * CARTEIRA de um cliente já identificado — não um upload genérico:
 *
 * - Exige cliente em foco (`id_cliente_app`) ANTES de aceitar o arquivo. Sem
 *   cliente resolvido no momento do anexo (chat GERAL do `/ia`, sem conversa
 *   vinculada), o upload não acontece e o anexo some do turno — só o texto
 *   é enviado. O composer mostra o aviso; não há contorno no front.
 * - Aceita só `.pdf .png .jpg .jpeg .webp .xlsx .xls .csv`, ≤20MB, ≤10
 *   arquivos por lote (`fee-fixo/constants/carteira-chat.js` — os limites
 *   REAIS de servidor). Os valores abaixo (`MAX_ANEXOS`, `EXTENSOES_
 *   PERMITIDAS`) continuam mais amplos — `.gif`, `.pptx` passam pela
 *   validação do COMPOSER e ficam sem upload no servidor, porque não há
 *   canal para eles. Isso é uma limitação de arquitetura, reportada ao
 *   orquestrador — não um bug deste arquivo.
 *
 * ★ [spec-010-r2-guiada-pelo-agente-com-transcricao, T006] `.txt` e `.docx`
 *   GANHARAM canal (`.md`/`.vtt` também, e por isso entraram na lista abaixo):
 *   o composer roteia por EXTENSÃO (`utils/canal-do-anexo.js`) para um dos
 *   dois canais que existem hoje — carteira
 *   (`POST /fee-fixo/clientes/carteira-chat/upload`) ou transcrição
 *   (`POST /v3/api/aawz-transcriptions/transcriptions/upload-texto`).
 *   `.gif`/`.pptx` seguem sem canal nenhum (limitação herdada, acima).
 */

// Teto por turno. Cinco é o mesmo teto do `perguntar_ao_gestor` e dos anexos de
// tarefa — a fila cabe numa faixa do composer sem virar segunda tela.
export const MAX_ANEXOS = 5

// Teto por arquivo. Mesmo valor da tarefa: é o tamanho em que um print ou um
// PDF de proposta cabem, e acima do qual o upload deixaria de ser instantâneo.
export const MAX_BYTES_POR_ANEXO = 10 * 1024 * 1024

export const EXTENSOES_DE_IMAGEM = ['png', 'jpg', 'jpeg', 'gif', 'webp']
export const EXTENSOES_DE_DOCUMENTO = [
  'pdf',
  'docx',
  'xlsx',
  'csv',
  'pptx',
  'txt',
  // [spec-010-r2-guiada-pelo-agente-com-transcricao, T006] — canal de
  // transcrição (`.txt`/`.docx` já estavam na lista; só estes dois entram).
  'md',
  'vtt',
]
export const EXTENSOES_PERMITIDAS = EXTENSOES_DE_IMAGEM.concat(
  EXTENSOES_DE_DOCUMENTO
)

// `accept` do input, derivado da allowlist — nunca redigitado. Lista paralela
// divergiria da allowlist no primeiro tipo novo, e o sintoma seria mudo: o
// seletor deixaria escolher um arquivo que a validação recusa em seguida.
export const ACEITA = EXTENSOES_PERMITIDAS.map((ext) => '.' + ext).join(',')

// Vocabulário do status de upload de um anexo do composer — a MESMA string viaja
// do mixin de upload (`send-message-anexos-upload-mixin.js`) até quem desenha
// (`composer-attachments.vue`) e até as leituras puras de `utils/anexos-da-fila.js`.
// Um typo ('enviada' × 'enviado') passava em silêncio e o anexo nunca entrava em
// `anexosProntos`.
export const STATUS_UPLOAD = Object.freeze({
  ENVIANDO: 'enviando',
  ENVIADO: 'enviado',
  ERRO: 'erro',
  SEM_CLIENTE: 'sem_cliente',
})
